"use client"

/**
 * Library **Folders** tree — shared outline primitives (`OutlineTree*`) so the rail
 * matches `HubTreePanelView` and shadcn sidebar file-tree rhythm.
 *
 * Indent model: flat `OutlineTreeSub` lists (full width) + `--outline-tree-depth-step`
 * (12px) on each row. Avoids compounding 24px sub-margins that crush deep labels.
 */

import * as React from "react"
import { Link } from "react-router"
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { SidebarNavLabel } from "@/components/ui/sidebar-nav-label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tip } from "@/components/ui/tip"
import { cn } from "@/lib/utils"
import {
  OutlineTreeCollapsibleContentRail,
  OutlineTreeMenuItem,
  OutlineTreeSub,
  outlineTreeBranchDepthStyle,
} from "@/components/data-views/outline-tree-menu"
import type { LibraryFolder } from "@/lib/mock/library-folders"
import { LIBRARY_FOLDER_ICON_COLORS } from "@/lib/mock/library-folders"
import {
  isLibraryNavActive,
  libraryHubScopeHref,
  type LibraryNavState,
} from "@/lib/library-nav"
import { libraryFolderMatchesSearch } from "@/components/data-views/library-folder-tree-branch-shared"

/** Root rows align with All/My (`px-2` on the link only); nested rows add depth step. */
function libraryFolderRowIndentStyle(depth: number): React.CSSProperties | undefined {
  if (depth <= 0) return undefined
  return {
    "--outline-tree-depth": depth,
    paddingInlineStart: `calc(${depth} * var(--outline-tree-depth-step))`,
  } as React.CSSProperties
}

export interface LibraryFolderPickFolderProps {
  /** One or more selected folder ids (multi-select pickers use the full list). */
  selectedFolderIds: string[]
  onSelect: (folderId: string) => void
  /** When set, branches with no name/descendant match are hidden. */
  searchQuery?: string
  /** Multi-select pickers show a checkbox; single-select uses row highlight only. */
  showCheckboxes?: boolean
}

export interface LibraryFolderTreeBranchProps {
  folder: LibraryFolder
  folders: LibraryFolder[]
  pathname: string
  hubSearchParams: URLSearchParams
  nav: LibraryNavState
  canManageFolders: boolean
  canManageAccess: boolean
  onAddSubfolder: (parentId: string) => void
  onCustomizeFolder: (folder: LibraryFolder) => void
  onManageAccess: () => void
  onDeleteFolder: (folder: LibraryFolder) => void
  onPinToTop?: (folder: LibraryFolder) => void
  onRenameFolder?: (folder: LibraryFolder) => void
  onMoveFolder?: (folder: LibraryFolder) => void
  pinnedFolderIds?: readonly string[]
  /** Nesting depth (0 = root folder under FOLDERS). */
  depth?: number
  /** Selection mode for folder pickers (e.g. new-question inspector). */
  pickFolder?: LibraryFolderPickFolderProps
  /** When set, branches with no name/descendant match are hidden (nav search). */
  searchQuery?: string
  /** Rolled-up bank question counts keyed by folder id. */
  folderItemCounts?: Readonly<Record<string, number>>
}

function FolderRowActionsMenu({
  folder,
  folderActive,
  canManageAccess,
  onAddSubfolder,
  onCustomizeFolder,
  onManageAccess,
  onDeleteFolder,
  onPinToTop,
  onRenameFolder,
  onMoveFolder,
  pinned = false,
  overlay = false,
  className,
}: {
  folder: LibraryFolder
  folderActive: boolean
  canManageAccess: boolean
  onAddSubfolder: (parentId: string) => void
  onCustomizeFolder: (folder: LibraryFolder) => void
  onManageAccess: () => void
  onDeleteFolder: (folder: LibraryFolder) => void
  onPinToTop?: (folder: LibraryFolder) => void
  onRenameFolder?: (folder: LibraryFolder) => void
  onMoveFolder?: (folder: LibraryFolder) => void
  pinned?: boolean
  overlay?: boolean
  className?: string
}) {
  const rename = onRenameFolder ?? onCustomizeFolder

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label={`Folder actions for ${folder.name}`}
          className={cn(
            overlay
              ? "icon-button-chrome absolute inset-0 flex shrink-0 items-center justify-center p-0 shadow-none"
              : "icon-button-chrome flex size-8 shrink-0 items-center justify-center p-0 shadow-none",
            "pointer-events-none opacity-0 transition-opacity",
            "group-hover/row:pointer-events-auto group-hover/row:opacity-100",
            "group-focus-within/row:pointer-events-auto group-focus-within/row:opacity-100",
            "data-[state=open]:pointer-events-auto data-[state=open]:opacity-100",
            folderActive && "hover:bg-sidebar-accent",
            className,
          )}
          onClick={event => event.stopPropagation()}
        >
          <i
            className="fa-light fa-ellipsis block text-xs leading-none"
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onPinToTop ? (
          <>
            <DropdownMenuItem
              onSelect={() => {
                window.setTimeout(() => onPinToTop(folder), 0)
              }}
            >
              <i className="fa-light fa-thumbtack text-xs" aria-hidden="true" />
              {pinned ? "Unpin from top" : "Pin to top"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem
          onSelect={() => {
            window.setTimeout(() => onAddSubfolder(folder.id), 0)
          }}
        >
          <i className="fa-light fa-folder-plus text-xs" aria-hidden="true" />
          New Subfolder
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canManageAccess}
          onSelect={() => {
            window.setTimeout(() => onManageAccess(), 0)
          }}
        >
          <i className="fa-light fa-user-gear text-xs" aria-hidden="true" />
          Manage Access
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            window.setTimeout(() => rename(folder), 0)
          }}
        >
          <i className="fa-light fa-pen text-xs" aria-hidden="true" />
          Rename
        </DropdownMenuItem>
        {onMoveFolder ? (
          <DropdownMenuItem
            onSelect={() => {
              window.setTimeout(() => onMoveFolder(folder), 0)
            }}
          >
            <i className="fa-light fa-box-archive text-xs" aria-hidden="true" />
            Move to subfolder
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={() => {
            window.setTimeout(() => onDeleteFolder(folder), 0)
          }}
        >
          <i className="fa-light fa-trash text-xs" aria-hidden="true" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function folderBranchHoldsSelection(
  folderId: string,
  folders: LibraryFolder[],
  selectedIds: readonly string[],
): boolean {
  if (selectedIds.includes(folderId)) return true
  for (const child of folders) {
    if (
      child.parentId === folderId &&
      folderBranchHoldsSelection(child.id, folders, selectedIds)
    ) {
      return true
    }
  }
  return false
}

function LibraryFolderTreeBranchInner({
  folder,
  folders,
  pathname,
  hubSearchParams,
  nav,
  canManageFolders,
  canManageAccess,
  onAddSubfolder,
  onCustomizeFolder,
  onManageAccess,
  onDeleteFolder,
  onPinToTop,
  onRenameFolder,
  onMoveFolder,
  pinnedFolderIds,
  depth = 0,
  pickFolder,
  searchQuery,
  folderItemCounts,
}: LibraryFolderTreeBranchProps) {
  const childFolders = React.useMemo(
    () =>
      folders
        .filter(f => f.parentId === folder.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [folders, folder.id],
  )

  const effectiveSearchQuery = pickFolder?.searchQuery ?? searchQuery
  if (
    effectiveSearchQuery?.trim() &&
    !libraryFolderMatchesSearch(folder, folders, effectiveSearchQuery)
  ) {
    return null
  }

  const hasSubfolders = childFolders.length > 0

  const folderHref = libraryHubScopeHref(pathname, hubSearchParams, {
    scope: "folder",
    folderId: folder.id,
  })
  const folderActive = pickFolder
    ? pickFolder.selectedFolderIds.includes(folder.id)
    : isLibraryNavActive(pathname, nav, "folder", folder.id, folders)
  const pinned = pinnedFolderIds?.includes(folder.id) ?? false
  const questionCount = folderItemCounts?.[folder.id] ?? 0
  const showCount = folderItemCounts != null

  const openForSelection = pickFolder
    ? folderBranchHoldsSelection(folder.id, folders, pickFolder.selectedFolderIds)
    : false

  return (
    <Collapsible defaultOpen={openForSelection} className="group/collapsible">
      <div
        className={cn(
          "group/row relative flex min-h-8 min-w-0 w-full items-start rounded-md transition-colors",
          folderActive
            ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground ring-1 ring-inset ring-sidebar-border/80"
            : "text-sidebar-foreground hover:bg-sidebar-accent/50",
        )}
        style={libraryFolderRowIndentStyle(depth)}
      >
        {hasSubfolders ? (
          <div className="flex w-7 shrink-0 items-start justify-center pt-1.5">
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="size-7 items-start justify-center bg-transparent p-0 shadow-none hover:bg-transparent active:bg-transparent data-[state=open]:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                aria-label={`${folder.name} — expand or collapse`}
              >
                <i
                  className="fa-light fa-chevron-right block text-xs leading-none transition-transform duration-150 group-data-[state=open]/collapsible:rotate-90"
                  aria-hidden
                />
              </Button>
            </CollapsibleTrigger>
          </div>
        ) : (
          <span className="inline-flex w-7 shrink-0 self-stretch" aria-hidden />
        )}
        <Tip label={folder.name} side="right" triggerClassName="flex min-h-8 min-w-0 flex-1 items-start">
          {pickFolder ? (
            <button
              type="button"
              role="option"
              aria-selected={folderActive}
              onClick={() => pickFolder.onSelect(folder.id)}
              className={cn(
                "flex h-auto min-h-8 min-w-0 flex-1 items-start gap-1.5 py-1 text-left text-sm transition-colors",
                "pe-0.5",
                "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                !folderActive && "text-sidebar-foreground",
              )}
            >
              <span
                className="mt-0.5 flex size-4 shrink-0 items-center justify-center self-start"
                aria-hidden
              >
                <i
                  className={cn(
                    "fa-light block text-xs leading-none",
                    folder.icon,
                    LIBRARY_FOLDER_ICON_COLORS[folder.colorKey],
                  )}
                  aria-hidden
                />
              </span>
              <SidebarNavLabel className="min-w-0">{folder.name}</SidebarNavLabel>
            </button>
          ) : (
            <Link
              to={folderHref}
              aria-current={folderActive ? "page" : undefined}
              className={cn(
                "flex h-auto min-h-8 min-w-0 flex-1 items-start gap-1.5 py-1 pe-0.5 text-left text-sm transition-colors",
                "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                !folderActive && "text-sidebar-foreground",
              )}
            >
              <span
                className="mt-0.5 flex size-4 shrink-0 items-center justify-center self-start"
                aria-hidden
              >
                <i
                  className={cn(
                    "fa-light block text-xs leading-none",
                    folder.icon,
                    LIBRARY_FOLDER_ICON_COLORS[folder.colorKey],
                  )}
                  aria-hidden
                />
              </span>
              <SidebarNavLabel className="min-w-0">{folder.name}</SidebarNavLabel>
            </Link>
          )}
        </Tip>
        {pickFolder?.showCheckboxes ? (
          <div
            className="relative me-0.5 flex w-8 shrink-0 self-stretch items-center justify-center"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Checkbox
              checked={folderActive}
              onCheckedChange={() => pickFolder.onSelect(folder.id)}
              aria-label={`Select ${folder.name}`}
            />
          </div>
        ) : showCount || canManageFolders ? (
          <div className="relative me-0.5 flex w-8 shrink-0 self-stretch items-center justify-center">
            {showCount ? (
              <span
                className={cn(
                  "text-xs tabular-nums text-muted-foreground transition-opacity",
                  canManageFolders &&
                    "group-hover/row:opacity-0 group-focus-within/row:opacity-0",
                )}
              >
                {questionCount}
              </span>
            ) : null}
            {canManageFolders ? (
              <FolderRowActionsMenu
                folder={folder}
                folderActive={folderActive}
                canManageAccess={canManageAccess}
                onAddSubfolder={onAddSubfolder}
                onCustomizeFolder={onCustomizeFolder}
                onManageAccess={onManageAccess}
                onDeleteFolder={onDeleteFolder}
                onPinToTop={onPinToTop}
                onRenameFolder={onRenameFolder}
                onMoveFolder={onMoveFolder}
                pinned={pinned}
                overlay={showCount}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      {hasSubfolders ? (
        <OutlineTreeCollapsibleContentRail>
          <OutlineTreeSub surface="sidebar" guideLayout="chevronRail" className="gap-0.5 py-0">
            {childFolders.map(child => (
              <OutlineTreeMenuItem
                key={child.id}
                style={outlineTreeBranchDepthStyle(depth + 1)}
              >
                <LibraryFolderTreeBranch
                  depth={depth + 1}
                  folder={child}
                  folders={folders}
                  pathname={pathname}
                  hubSearchParams={hubSearchParams}
                  nav={nav}
                  canManageFolders={canManageFolders}
                  canManageAccess={canManageAccess}
                  onAddSubfolder={onAddSubfolder}
                  onCustomizeFolder={onCustomizeFolder}
                  onManageAccess={onManageAccess}
                  onDeleteFolder={onDeleteFolder}
                  onPinToTop={onPinToTop}
                  onRenameFolder={onRenameFolder}
                  onMoveFolder={onMoveFolder}
                  pinnedFolderIds={pinnedFolderIds}
                  pickFolder={pickFolder}
                  searchQuery={searchQuery}
                  folderItemCounts={folderItemCounts}
                />
              </OutlineTreeMenuItem>
            ))}
          </OutlineTreeSub>
        </OutlineTreeCollapsibleContentRail>
      ) : null}
    </Collapsible>
  )
}

/**
 * Memoized for the secondary panel: the tree re-renders on every navigation,
 * hash change, or searchParams flip in `LibrarySecondaryNav`. Without memo,
 * each level recurses through `Collapsible` + `Tip` + `DropdownMenu` per
 * branch (an inactive subtree of ~10–20 folders is ~30+ Radix instances) and
 * pegs frame budget on otherwise unrelated route changes.
 *
 * Memoization is safe with reference equality because the parent passes
 * stable refs:
 *   - `folder`, `folders` — from `libraryFolderBridge?.folders ?? DEFAULT…`
 *     (state, not recomputed each render).
 *   - `pathname` — string from `useLocation()`.
 *   - `hubSearchParams` — react-router v7 `useSearchParams` memoizes
 *     via `useMemo` on `location.search` (see chunk-66UKHEGQ.js#L716).
 *   - `nav` — `useMemo` over `searchParams.toString()` in `LibrarySecondaryNav`.
 *   - `canManageFolders` / `canManageAccess` — booleans.
 *   - All four callbacks — wrapped in `useCallback` in `LibrarySecondaryNav`.
 *
 * Recursive children render `<LibraryFolderTreeBranch>` (the exported
 * memoized const), so memoization composes down the tree.
 */
export const LibraryFolderTreeBranch = React.memo(LibraryFolderTreeBranchInner)
